import { ApolloQueryResult, gql } from "@apollo/client";
import { Layout } from "components/layout";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { GetServerSideProps } from "next";
import {
  ProductCardDataFragmentDoc,
  GetProductDocument,
  GetProductQueryVariables,
  useGetProductQuery,
  GetProductQueryResult,
  useGetAddressForAccountQuery,
  useGetAddressForAccountLazyQuery,
} from "lib/graphql-generated";
import { emailRegex, parseParam, range } from "lib/utils";
import { Spinner } from "components/spinner";
import currency from "currency.js";
import { Button } from "components/buttons/button";
import { SharingBar } from "components/sharing-bar";
import { SubscribeButton } from "components/buttons/subscribe-button";
import { IBasket, IBasketItem } from "lib/basket/types";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useUser } from "lib/hooks/use-user";
import { StandardInput } from "components/forms/input";
import { useInput } from "lib/hooks/use-input";
import {
  ShippingSelector,
  ShippingSelectorValue,
} from "components/forms/shipping-selector";
import { CountrySelector } from "components/forms/country-select";
import { useRouter } from "next/router";
import { useIsInBasket } from "lib/hooks/basket/use-is-in-basket";
import { useBasket } from "lib/hooks/basket/use-basket";
import { RenderContent } from "components/content-renderer";

const GET_PRODUCT = gql`
  query GetProduct($slug: String!) {
    products(
      where: {
        _and: [{ slug: { _eq: $slug } }, { is_deleted: { _eq: false } }]
      }
    ) {
      description
      ...ProductCardData
    }
  }
  ${ProductCardDataFragmentDoc}
`;

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);

interface Props {
  slug: string;
}
export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  const slug = parseParam(ctx.params.slug);

  const { data }: ApolloQueryResult<GetProductQueryResult["data"]> =
    await client.query({
      query: GetProductDocument,
      variables: {
        slug: slug,
      } as GetProductQueryVariables,
    });

  if (data.products.length > 0) {
    return addApolloState(client, {
      props: { slug: slug } as Props,
    });
  } else {
    return {
      notFound: true,
    };
  }
};

export const ProductPage = ({ slug }: Props) => {
  const { data, loading, error, networkStatus } = useGetProductQuery({
    variables: { slug },
  });

  const product = data?.products[0];
  const [basket, modifyBasket] = useBasket();
  const isInBasket = useIsInBasket(product?.id);

  return (
    <Layout pageName={product.title}>
      {loading ? (
        <section className='w-screen-2xl mx-auto flex justify-center py-20'>
          <Spinner size={20} />
        </section>
      ) : product ? (
        <>
          <article className='[ order-1 grid grid-cols-1 gap-y-6 py-16 ] [ md:grid-cols-5 md:gap-y-8 md:max-w-screen-md md:px-4 md:pb-8 ] [ lg:max-w-screen-xl lg:grid-cols-7 lg:gap-x-8 lg:order-3 ] w-full justify-items-center mx-auto'>
            <img
              alt={`Cover for: ${product.title}`}
              className='max-w-2xs sm:max-w-xs [ md:max-w-full md:col-span-2 ] [ ]'
              src={product.image}
            />

            <section className='[ max-w-2xs sm:max-w-xs flex flex-col ] [ md:mb-auto md:flex-col md:col-span-3 ] [ lg:col-span-2 lg:col-start-6 ] flex-wrap '>
              <h1 className='max-w-2xs sm:max-w-xs'>
                <span className='bg-green-default text-white font-header tracking-wide uppercase pt-1 px-1 whitespace-pre-wrap text-2xl'>
                  {product.title}
                </span>
              </h1>

              <span className='my-auto uppercase font-header tracking-wide text-2xl md:mt-4'>
                {currency(product.price, {
                  symbol: "£",
                  fromCents: true,
                }).format()}
              </span>

              <Button
                className='md:mt-4 ml-auto my-auto md:ml-0'
                buttonType='primary'
                buttonSize='md'
                disabled={isInBasket}
                onClick={() =>
                  modifyBasket({
                    type: "add",
                    payload: {
                      type: "product",
                      id: product.id,
                      data: {
                        price: product.price,
                        discount: product.is_discounted
                          ? product.discount_value
                          : 0,
                        quantity: 1,
                        product: {
                          id: product.id,
                          image: product.image,
                          title: product.title,
                        },
                      },
                    },
                  })
                }>
                {isInBasket ? "In Basket" : "Add to basket"}
              </Button>

              <section className='mt-4 md:mt-4 w-full max-w-2xs [ md:max-w-full pt-4 border-t border-black ] flex flex-row justify-between md:space-x-8 md:justify-start'>
                <span className='hidden md:block my-auto text-gray-500'>
                  Share:
                </span>
                <SharingBar
                  className='h-6'
                  url={`/shop/${product.slug}`}
                  text={`Checkout this great product sold by Lighthouse Bookshop (https://lighthousebookshop.com): ${product.title}`}
                />
              </section>
            </section>
            {product.description && product.description !== "" && (
              <section className='prose px-8 md:col-span-5 md:col-start-1 justify-self-start md:px-0  [ lg:col-start-3 lg:col-span-3 lg:row-start-1 ] '>
                <RenderContent content={product.description} />
              </section>
            )}
          </article>
          {/* <section className='[ order-3 ] w-full p-8 bg-green-very-light lg:order-4'>
            <div className='space-y-6 md:space-y-0 max-w-screen-xl mx-auto md:grid md:grid-cols-2 md:gap-8 lg:grid-cols-4 '>
              <h2 className='col-span-full uppercase tracking-wide font-header text-2xl md:w-full'>
                Spread the word - with our new merchandise
              </h2>
              {range(4).map((e, i) => (
                <article className='max-w-2xs mx-auto lg:ml-0 space-y-4 '>
                  <img
                    alt={`Image for book: ${product.title}`}
                    src={product.image}
                  />
                  <section className='border-t-8 pt-4 border-black font-header uppercase tracking-wide flex flero justify-between'>
                    <span>
                      {' '}
                      {currency(product.price, {
                        symbol: '£',
                        fromCents: true,
                      }).format()}
                    </span>
                    <Button className='mb-auto uppercase tracking-wide font-header'>
                      Add to basket
                    </Button>
                  </section>
                </article>
              ))}
              <section className='max-w-2xs md:max-w-full md:w-full md:col-span-full md:border-0 mx-auto border-t border-black text-center py-8 flex flex-col'>
                <span className='uppercase tracking-wide font-header text-xl mb-2'>
                  Browse more books
                </span>
                <Button
                  className=' mx-auto'
                  buttonType='secondary'
                  buttonSize='lg'>
                  Here
                </Button>
              </section>
            </div>
          </section> */}
        </>
      ) : (
        "not found"
      )}
    </Layout>
  );
};

export default ProductPage;
